/**
 * Organizations API service - Group accounts management
 */
import apiClient from './client';

// ========================================
// TypeScript Interfaces
// ========================================

export interface Organization {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  max_subuser_slots: number;
  used_subuser_slots: number;
  subscription_tier: string;
  email_domain_whitelist: string[];
  require_email_verification: boolean;
  chargekeep_customer_id?: string;
  chargekeep_subscription_id?: string;
  created_at: string;
  updated_at: string;
}

export interface OrganizationStats {
  total_slots: number;
  used_slots: number;
  available_slots: number;
  member_count: number;
  pending_invitations: number;
  subscription_tier: string;
  conversations_limit: number;
  ai_requests_limit: number;
  created_at: string;
}

export interface SubuserQuota {
  id: string;
  user_id: string;
  organization_id: string;
  allocated_clipboard_slots: number;
  allocated_conversations: number;
  allocated_ai_requests: number;
  used_clipboard_slots: number;
  used_conversations: number;
  used_ai_requests: number;
  created_at: string;
  updated_at: string;
}

export interface Member {
  id: string;
  email: string;
  full_name?: string;
  preferred_name?: string;
  user_role: string;
  organization_id?: string;
  parent_user_id?: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

export interface MemberWithQuota {
  member: Member;
  quota?: SubuserQuota;
}

export interface Invitation {
  id: string;
  organization_id: string;
  invited_by: string;
  email: string;
  role: string;
  expires_at: string;
  consumed: boolean;
  accepted_at?: string;
  rejected_at?: string;
  created_at: string;
  is_expired: boolean;
  is_valid: boolean;
}

export interface InvitationCreated {
  invitation: Invitation;
  invitation_url: string;
  raw_token: string;
}

// Request types
export interface CreateOrganizationRequest {
  name: string;
  slug: string;
  subscription_tier: string;
  email_domain_whitelist?: string[];
}

export interface UpdateOrganizationRequest {
  name?: string;
  email_domain_whitelist?: string[];
  require_email_verification?: boolean;
}

export interface CreateInvitationRequest {
  email: string;
  role?: string;
  expiry_days?: number;
}

export interface UpdateQuotaRequest {
  allocated_clipboard_slots?: number;
  allocated_conversations?: number;
  allocated_ai_requests?: number;
}

export interface AcceptInvitationRequest {
  token: string;
  full_name?: string;
  password?: string;
}

// ========================================
// API Functions
// ========================================

export const organizationsApi = {
  // Organization CRUD
  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    const response = await apiClient.post('/api/organizations', data);
    return response.data;
  },

  async getMyOrganization(): Promise<Organization> {
    const response = await apiClient.get('/api/organizations/me');
    return response.data;
  },

  async getOrganization(id: string): Promise<Organization> {
    const response = await apiClient.get(`/api/organizations/${id}`);
    return response.data;
  },

  async updateOrganization(id: string, data: UpdateOrganizationRequest): Promise<Organization> {
    const response = await apiClient.patch(`/api/organizations/${id}`, data);
    return response.data;
  },

  async deleteOrganization(id: string): Promise<void> {
    await apiClient.delete(`/api/organizations/${id}`);
  },

  // Stats & Members
  async getOrganizationStats(id: string): Promise<OrganizationStats> {
    const response = await apiClient.get(`/api/organizations/${id}/stats`);
    return response.data;
  },

  async listMembers(id: string, includeAdmin: boolean = true): Promise<MemberWithQuota[]> {
    const response = await apiClient.get(`/api/organizations/${id}/members`, {
      params: { include_admin: includeAdmin }
    });
    return response.data;
  },

  async removeSubuser(id: string, userId: string): Promise<void> {
    await apiClient.delete(`/api/organizations/${id}/members`, {
      data: { user_id: userId }
    });
  },

  // Quota Management
  async getSubuserQuota(orgId: string, userId: string): Promise<SubuserQuota> {
    const response = await apiClient.get(`/api/organizations/${orgId}/members/${userId}/quota`);
    return response.data;
  },

  async updateSubuserQuota(orgId: string, userId: string, data: UpdateQuotaRequest): Promise<SubuserQuota> {
    const response = await apiClient.patch(`/api/organizations/${orgId}/members/${userId}/quota`, data);
    return response.data;
  },

  // Ownership Transfer
  async transferOwnership(id: string, newOwnerId: string): Promise<Organization> {
    const response = await apiClient.post(`/api/organizations/${id}/transfer-ownership`, {
      new_owner_id: newOwnerId
    });
    return response.data;
  },

  // Invitations
  async createInvitation(data: CreateInvitationRequest): Promise<InvitationCreated> {
    const response = await apiClient.post('/api/invitations', data);
    return response.data;
  },

  async listInvitations(organizationId?: string, pendingOnly: boolean = true): Promise<Invitation[]> {
    const response = await apiClient.get('/api/invitations', {
      params: { organization_id: organizationId, pending_only: pendingOnly }
    });
    return response.data;
  },

  async cancelInvitation(invitationId: string): Promise<void> {
    await apiClient.delete(`/api/invitations/${invitationId}`);
  },

  async acceptInvitation(data: AcceptInvitationRequest): Promise<{ message: string; user_id: string; organization_id: string; organization_name: string }> {
    const response = await apiClient.post('/api/invitations/accept', data);
    return response.data;
  },

  async rejectInvitation(token: string): Promise<void> {
    await apiClient.post('/api/invitations/reject', { token });
  },

  async cleanupExpiredInvitations(): Promise<{ message: string; deleted_count: number }> {
    const response = await apiClient.post('/api/invitations/cleanup');
    return response.data;
  },
};
